import { memo, useCallback } from "react";

import { FormType, AvailablePaymentCurrency } from "@simplyk/common";

import { getRateExtensionInfo } from "../AuctionDetailDrawer/bid-status.helper";

import { useSalesOpenSchedule } from "@/canvas/features/FormV2/blocks/campaign-details/SalesOpeningChip/useSalesOpenSchedule";
import { AuctionRateCard } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/AuctionRateCard/AuctionRateCard";
import { useAuctionRateCard } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/AuctionRateCard/useAuctionRateCard";
import { AntiSnipingInfo } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AntiSnipingInfo";
import { AuctionRateClosedInfo } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/AuctionRateClosedInfo";
import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { isRateClosed } from "@/canvas/features/FormV2/helpers/useIsRateClosed";
import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { RateOutput } from "@/common/types/trpc";

interface AuctionRateItemProps {
  rate: RateOutput;
  formCurrency: AvailablePaymentCurrency;
  maximumPurchasableTicketsForRate: number;
  localBidAmountInCents?: number;
  autoBidMaxAmountInCents?: number;
  isAutoBidBadge?: boolean;
  hasServerAutoBid?: boolean;
  ticketingIsClosed: boolean;
  onClickRate: (rateId: string) => void;
}

const AuctionRateItemInner = ({
  rate,
  formCurrency,
  maximumPurchasableTicketsForRate,
  localBidAmountInCents,
  autoBidMaxAmountInCents,
  isAutoBidBadge = false,
  hasServerAutoBid = false,
  ticketingIsClosed,
  onClickRate,
}: AuctionRateItemProps) => {
  const { formObject: ticketing } = useFormV2Context(FormType.Ticketing);
  const { highestBidsMapByRateId } = useFrontendAuctionContext();
  const rateData = highestBidsMapByRateId?.[rate.id];
  const { isExtended, effectiveEndUtc: extendedEndUtc } = getRateExtensionInfo(rateData);
  const rateClosed = isRateClosed(rateData?.effectiveEndUtc);
  const { salesOpenDate, isSalesScheduled } = useSalesOpenSchedule();

  const itemData = useAuctionRateCard({
    rate,
    formCurrency,
    maximumPurchasableTicketsForRate,
    ticketingIsClosed,
    localBidAmountInCents,
    autoBidMaxAmountInCents,
  });

  const displayBidHistory = ticketing?.displayBidHistory !== false;

  const antiSnipingInfo = isExtended ? (
    <AntiSnipingInfo effectiveEndUtc={extendedEndUtc!} timezone={ticketing?.eventTimezone ?? null} />
  ) : rateClosed ? (
    <AuctionRateClosedInfo />
  ) : undefined;

  const handleClick = useCallback(() => onClickRate(rate.id), [onClickRate, rate.id]);
  const isCardInteractive = !rateClosed && !ticketingIsClosed && !isSalesScheduled;

  return (
    <AuctionRateCard
      {...itemData}
      topBidderName={displayBidHistory ? itemData.topBidderName : undefined}
      isAutoBidBadge={isAutoBidBadge}
      hasServerAutoBid={hasServerAutoBid}
      ticketingIsClosed={ticketingIsClosed}
      isRateClosed={rateClosed}
      salesOpenDate={salesOpenDate}
      eventTimezone={ticketing?.eventTimezone ?? null}
      antiSnipingInfo={antiSnipingInfo}
      onDetailsClick={isCardInteractive ? handleClick : undefined}
      onBidClick={isCardInteractive ? handleClick : undefined}
    />
  );
};

export const AuctionRateItem = memo(AuctionRateItemInner);
AuctionRateItem.displayName = "AuctionRateItem";
